import { z } from 'zod';
import { AccountClassification, ManualCOAInput } from '../../../../../types/entity';

/**
 * Zod schema representing a single account to import. It transforms what the user uploads via c
 */
const AccountImportSchema = z
    .object({
        // Code must be a string with at least 1 character
        code: z.string().trim().min(1, { message: 'A unique code is required.' }),
        // Name must be a string with at least 1 character
        name: z.string().trim().min(1, { message: 'A unique name is required.' }),
        // Description is optional
        description: z.string().trim().optional(),
        // Report code must be a string with at least 1 character
        reportCode: z.string().trim().min(1, { message: 'A report code is required.' }),
        // Type must be a string with at least 1 character
        type: z.preprocess((v) => String(v).toUpperCase().trim(), z.string().min(1, { message: 'A type is required.' })),
        // Class must be one of AccountClassification, it will be transformed to upper case to match the enum
        class: z.preprocess(
            (v) => String(v).toUpperCase().trim(),
            z.nativeEnum(AccountClassification, {
                errorMap: () => {
                    return { message: `Class must be one of: ${Object.keys(AccountClassification).join(', ')}.` };
                },
            })
        ),
    })
    .transform((row) => {
        return {
            ...row,
            description: row.description || undefined, // Handle empty string cases
        };
    });

const ManualImportSchema = z.array(AccountImportSchema);

/**
 * Validates a CSV file and returns a list of accounts to import.
 * Ensures values are the correct type and that there are no duplicate codes and names.
 *
 * @param input The CSV file to validate
 * @returns A list of accounts to import or a list of error messages
 */
export function validateCsv(
    input: unknown
):
    | { success: true; error: undefined; data: ManualCOAInput }
    | { success: false; data: undefined; error: { issues: { line: number; errors: { field: string; message: string }[] }[] } } {
    const parsed = ManualImportSchema.safeParse(input);

    const errors: { field: string; message: string }[][] = [];
    // Add the normal zod errors to the correct index
    parsed?.error?.errors.forEach((e) => {
        const idx = +e.path[0];
        if (typeof idx === 'number') {
            if (!errors[idx]) {
                errors[idx] = [];
            }
            errors[idx].push({ field: e.path[1]?.toString(), message: e.message });
        }
    });

    // Check for duplicates
    if (Array.isArray(input)) {
        const codes: string[] = [];
        const names: string[] = [];

        input.forEach((item, idx) => {
            const code = item?.code?.toLowerCase();
            const name = item?.name?.toLowerCase();

            if (typeof code !== 'string' || typeof name !== 'string') {
                return;
            }

            if (codes.includes(code)) {
                // Add to the error index
                if (!errors[idx]) {
                    errors[idx] = [];
                }
                errors[idx].push({ field: 'code', message: `Duplicate code: ${item.code}.` });
            } else {
                codes.push(code);
            }
            if (names.includes(name)) {
                // Add to the error index
                if (!errors[idx]) {
                    errors[idx] = [];
                }
                errors[idx].push({ field: 'name', message: `Duplicate name: ${item.name}.` });
            } else {
                names.push(code);
            }
        });
    }

    const isSuccess = errors.length === 0;

    if (isSuccess) {
        return {
            success: true,
            data: parsed.data ?? [],
            error: undefined,
        };
    }

    return {
        success: false,
        data: undefined,
        error: {
            issues: errors.map((e, idx) => {
                return { line: idx + 1, errors: e };
            }),
        },
    };
}
